import {
  ArrayInput,
  AutocompleteInput,
  Button,
  minValue,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  useNotify,
  useRefresh,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useWatch } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { Grid } from "@mui/material";

import { settings } from "app";
import { erasmusRequests } from "api/erasmusApi";
import { studentRequests } from "api/studentApi";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";

// The form is filled from the record, so the payload is the whitelist - the row id of an existing
// discipline is not part of the request.
const toPayload = (disciplines = []) =>
  disciplines.map(discipline => ({
    disciplineId: discipline?.disciplineId,
    semester: discipline?.semester,
    studyGroup: discipline?.studyGroup,
  }));

const DisciplineFields = ({ curriculumId, watchPrefix }) => {
  const disciplineId = useWatch({ name: `${watchPrefix}.disciplineId` });

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="disciplineId"
          reference={studentRequests.getCurriculumDisciplinesSelect(curriculumId)}
        >
          <AutocompleteInput
            label="Дисциплина"
            optionText="name"
            fullWidth
            validate={required()}
            filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
          />
        </ReferenceInput>
      </Grid>
      {/* The semesters depend on the picked discipline, so the input appears only after it. */}
      {disciplineId ? (
        <Grid
          item
          xs={12}
          md={3}
        >
          <ReferenceInput
            source="semester"
            reference={studentRequests.getCurriculumDisciplineSemestersSelect(curriculumId, disciplineId)}
          >
            <SelectInput
              label="Семестър"
              optionText="name"
              optionValue="id"
              fullWidth
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
      ) : null}
      <Grid
        item
        xs={12}
        md={3}
      >
        <NumberInput
          source="studyGroup"
          label="Учебна група"
          fullWidth
          min={1}
          validate={[required(), minValue(1)]}
          onWheel={e => e.target.blur()}
        />
      </Grid>
    </Grid>
  );
};

const DisciplineIteratorItem = ({ curriculumId }) => {
  const { index } = useSimpleFormIteratorItem();

  return (
    <DisciplineFields
      curriculumId={curriculumId}
      watchPrefix={`education.disciplines.${index}`}
    />
  );
};

const ChangeDisciplinesDialog = ({ id, curriculumId }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSave = async data => {
    const { data: response } = await dataProvider.customRequest(
      erasmusRequests.updateIncomingStudentsRegularDisciplines(id),
      { disciplines: toPayload(data?.education?.disciplines) },
      "PUT",
    );

    notify("Дисциплините са променени успешно", { type: "success" });

    // the conflicts do not block the change - the coordinator only needs to know about them
    const warnings = response?.scheduleConflictWarnings ?? [];

    if (warnings.length) notify(warnings.join("\n"), { type: "warning", multiLine: true, autoHideDuration: 10000 });

    refresh();
  };

  return (
    <FormModal
      titleContent="Промяна на дисциплини"
      dialogSize="md"
      onConfirm={handleSave}
      triggerButton={
        <Button
          label="Промени дисциплините"
          variant="contained"
          startIcon={<EditIcon />}
        />
      }
    >
      <ArrayInput
        source="education.disciplines"
        label={false}
        fullWidth
        validate={required("Моля добавете поне една дисциплина!")}
      >
        <SimpleFormIterator
          inline={false}
          disableReordering
          sx={settings.style.iteratorWithoutGap}
        >
          <DisciplineIteratorItem curriculumId={curriculumId} />
        </SimpleFormIterator>
      </ArrayInput>
    </FormModal>
  );
};

export default ChangeDisciplinesDialog;
